import { Button } from '@flowus/common/components';
import { ScrollLoadTable } from '@flowus/common/components/scroll-load-list';
import { cx } from '@flowus/common/cx';
import { toDayjsDateFormat } from '@flowus/common/i18n';
import { useCloseModal, useOpenModal } from '@flowus/common/next-modal';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import { Modals } from '@flowus/shared';
import dayjs from 'dayjs';
import { type FC } from 'react';
import { Tooltip } from 'src/common/components/tooltip';
import { request } from 'src/common/request/request';
import { useOpenTransfer } from 'src/hooks/public/use-open-transfer';
import { useWallet } from 'src/hooks/user/use-wallet';
import { SettingModalCommon } from '../common';
import { useCloseSettingModal } from '../use-open-setting-modal';
import { InvitationInfo } from './invitation-info';
import { OrderRecordModal } from './order-record-modal';
import { getCurrentSpaceId } from 'src/hooks/space/get-space';
import { getCurrencySymbols } from '@flowus/common';

export const ProfitSetting: FC = () => {
  const { t } = useAppTranslation();
  const closeSettingModal = useCloseSettingModal();

  return (
    <>
      <div className={SettingModalCommon.commonStyles.container}>
        <SettingModalCommon.Header title={t('views.settings.profit.invitation_rebate')} />
        <MyProfitAccount />
        <SettingModalCommon.SettingDivider />
        <InvitationInfo />
      </div>
      <SettingModalCommon.FooterBar
        cancelText={t('common.close')}
        cancel={() => closeSettingModal()}
      />
    </>
  );
};

const MyProfitAccount: FC = () => {
  const { t } = useAppTranslation();
  const openModal = useOpenModal();
  const openTransfer = useOpenTransfer();
  const [wallet] = useWallet();

  const openTotalRecordModal = () => {
    openModal.modal({
      modalId: Modals.ORDER_RECORD_MODAL,
      content: () => <OrderRecordModal type="all" />,
    });
  };

  const openDrawingModal = () => {
    openModal.modal({
      modalId: Modals.ORDER_RECORD_MODAL,
      content: () => <DrawingModal />,
    });
  };

  return (
    <div className="text-t2 py-4 space-y-2.5">
      <div className="flex items-center justify-between">
        <div className="space-y-2 animate-click" onClick={openTotalRecordModal}>
          <div className="space-x-1 flex items-center">
            <span>{t('views.settings.profit.cumulative_commission')}</span>
            <SettingModalCommon.Arrow />
          </div>
          <span className="space-x-1">
            <span>{getCurrencySymbols()}</span>
            <span className="text-h2">{wallet?.totalAmount.toFixed(2)}</span>
          </span>
        </div>
        <div>
          <Button onClick={() => openTransfer({ type: 'user', spaceId: getCurrentSpaceId() })}>
            {t('views.settings.profit.withdraw')}
          </Button>
        </div>
      </div>
      <div className="flex items-center space-x-24">
        <div className="flex flex-col space-y-1">
          <span>{t('views.settings.profit.withdrawable_amount')}</span>
          <span className="space-x-1">
            <span>{getCurrencySymbols()}</span>
            <span className="text-h3">{wallet?.canWithdrawAmount.toFixed(2)}</span>
          </span>
        </div>
        <Tooltip
          popup={t('views.settings.profit.withdrawal_days')}
          className="cursor-help flex flex-col space-y-1"
        >
          <span className="border-b border-dashed border-grey3">
            {t('views.settings.profit.in_progress_amount')}
          </span>
          <span className="space-x-1">
            <span>{getCurrencySymbols()}</span>
            <span className="text-h3">{wallet?.inProgressAmount.toFixed(2)}</span>
          </span>
        </Tooltip>
        <div className="flex flex-col space-y-1 animate-click" onClick={openDrawingModal}>
          <span>
            <span>{t('views.settings.profit.withdrawn_amount')}</span>
            <SettingModalCommon.Arrow />
          </span>
          <span className="space-x-1">
            <span>{getCurrencySymbols()}</span>
            <span className="text-h3">{wallet?.alreadyWithdrawAmount.toFixed(2)}</span>
          </span>
        </div>
      </div>
    </div>
  );
};

/** 提现记录 */
const DrawingModal: FC = () => {
  const { t } = useAppTranslation();
  const closeModal = useCloseModal();
  const dateTimeFormat = `${toDayjsDateFormat(t('common.date_format_ymd_no_time'))} HH:mm`;

  const getTransferListApi = async (params: { pageIndex: number; pageSize: number }) => {
    const res = await request.infra.withdrawForVipOrderList(params.pageIndex, params.pageSize);

    return {
      more: res.more,
      list: res.list,
    };
  };

  return (
    <ScrollLoadTable
      className="min-w-[500px]"
      modalHeader={{
        title: t('common.withdrawal_records'),
        closeModal: () => closeModal(Modals.ORDER_RECORD_MODAL),
      }}
      getApi={getTransferListApi}
      titleList={[
        { title: t('common.amount') },
        { title: t('views.settings.profit.withdrawal_status') },
        { title: t('views.settings.profit.time') },
      ]}
      dateList={[
        {
          id: 'total',
          render: (item) => `${getCurrencySymbols()}${item.total}`,
        },
        {
          id: 'status',
          render: (item) => {
            const fail = item.status === 2;
            return (
              <span className={cx(fail && 'text-red')}>
                {fail
                  ? t('views.settings.profit.withdrawal_failed')
                  : t('views.settings.profit.withdrawal_success')}
              </span>
            );
          },
        },
        {
          id: 'createdAt',
          render: (item) => {
            return dayjs(item.createdAt).format(dateTimeFormat);
          },
        },
      ]}
    />
  );
};
